'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import type { Block, PostMeta } from '@/components/blog-editor/types';
import { BlockRenderer } from '@/components/blog-editor/BlockRenderer';
import { stripHtml } from '@/lib/strip-html';
import { ReadOnlyEditorProvider } from '@/components/blog-editor/BlogEditor';
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { Footer } from '@/components/seasons/Footer';
import { FlowNavBar } from '@/components/seasons/FlowNavBar';
import { FlowFooter } from '@/components/seasons/FlowFooter';
import { CometNavBar } from '@/components/seasons/CometNavBar';
import { CometFooter } from '@/components/seasons/CometFooter';
import { GalaxyNavBar } from '@/components/seasons/GalaxyNavBar';
import { GalaxyFooter } from '@/components/seasons/GalaxyFooter';
import { GravityNavBar } from '@/components/seasons/GravityNavBar';
import { GravityFooter } from '@/components/seasons/GravityFooter';
import { StoryhealthNavBar } from '@/components/seasons/StoryhealthNavBar';
import { StoryhealthFooter } from '@/components/seasons/StoryhealthFooter';
import { BlogShareInline } from '@/components/seasons/BlogShareInline';
import { Input } from '@/components/seasons/Input';
import { SuggestedResources, type ResourceCard } from '@/components/seasons/SuggestedResources';
import { whitepaperGradient } from '@/lib/whitepaper-gradient';
import { CaretRight } from '@phosphor-icons/react';

// ─── Animation constants ──────────────────────────────────────────────────────

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1];
const EASE_IN_OUT: [number, number, number, number] = [0.4, 0, 0.2, 1];
const T_PAGE: [number, number, number, number] = EASE_OUT;

// S2 draw-check constants
const S2_R = 24;
const S2_CIRC = 2 * Math.PI * S2_R;

// Thumbnail page-spread animation — V6 Lift & spread (3-state: rest / spread / downloaded)
const wpThumb = {
  rest:       { transition: { staggerChildren: 0.05, staggerDirection: -1 as const } },
  spread:     { transition: { staggerChildren: 0.06 } },
  downloaded: { transition: { staggerChildren: 0.08 } },
};

// Page pose definitions (back→mid→front, z:1→2→3)
// Spread positions are symmetric around 0 so the fan stays centred in the
// thumbnail card (back -15 / mid 0 / front +15) — previously the back went
// to -24 while the front only reached +6, which left visibly more empty
// space on the right than the left.
const PAGE_POSES = [
  { rest: { rotate: -3, x: '-5%', y: '0%' }, spread: { rotate: -8, x: '-10%', y: '0%' }, downloaded: { rotate: -6, x: '-14%', y: '0%' }, z: 1 },
  { rest: { rotate: -1, x: '-2%', y: '0%' }, spread: { rotate:  0, x:   '0%', y: '0%' }, downloaded: { rotate: -2, x:  '-5%', y: '0%' }, z: 2 },
  { rest: { rotate:  0, x:  '0%', y: '0%' }, spread: { rotate:  8, x:  '10%', y: '0%' }, downloaded: { rotate:  2, x:   '4%', y: '0%' }, z: 3 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface RelatedPost {
  slug: string;
  title: string;
  dek?: string;
  category?: string;
  date?: string;
  heroImg?: string;
  authorName?: string;
  pdfUrl?: string;
  pdfPageCount?: number;
}

interface Props {
  slug: string;
  meta: PostMeta;
  blocks: Block[];
  related: RelatedPost[];
  urlBrand?: string;
  /** Read server-side in page.tsx from the request headers — see getClientIp. */
  visitorIp?: string;
}

// ─── Category display name ────────────────────────────────────────────────────

const CATEGORY_DISPLAY: Record<string, string> = {
  'White Paper':     'whitepaper',
  'Case Study':      'case study',
  'Industry Report': 'industry report',
  'Brochure':        'brochure',
};

function categoryLabel(category?: string): string {
  return (category && CATEGORY_DISPLAY[category]) || 'document';
}

// ─── Resource body formatter ──────────────────────────────────────────────────
// Most resource MDX files were imported from Webflow as a single unstructured
// paragraph blob. This formatter recovers proper heading/bullet/paragraph
// structure using full sentence-level analysis — handles any future unstructured
// import without needing a migration script re-run.

const ZWJ_RE = /[‍­​﻿‌⁠]+/g;

const STOP_SET = new Set(['a','an','the','and','but','or','for','nor','on','at','to','by','in','of','up','as','is','it','its','be','we','he','she','they','this','that','these','those','from','with','about','into','through','during','before','after','above','below','between','while','when','if','so','yet','both']);
const CLAUSE_STARTERS = new Set(['as','with','while','when','because','since','although','but','however','so','yet','if']);

function sentenceSplit(text: string): string[] {
  return text.split(/(?<=[.!?])\s+(?=[A-Z])/).map(s => s.trim()).filter(Boolean);
}

function titleRatio(phrase: string): number {
  const words = phrase.split(/\s+/).filter(w => !STOP_SET.has(w.toLowerCase()));
  if (words.length < 2) return 0;
  return words.filter(w => /^[A-Z]/.test(w)).length / words.length;
}

function isHeadingPhrase(s: string): boolean {
  if (!s || s.length > 120) return false;
  if (/[.!?]$/.test(s)) return false;
  const wc = s.split(/\s+/).length;
  if (wc < 3 || wc > 15) return false;
  return titleRatio(s) >= 0.60;
}

function parseTermDesc(s: string): { term: string; desc: string } | null {
  const m = s.match(/^([A-Z][^:]{4,50}?)\s+:\s+(.{10,})$/);
  if (!m || titleRatio(m[1].trim()) < 0.45) return null;
  return { term: m[1].trim(), desc: m[2].trim() };
}

function splitHeadingBody(s: string): { heading: string; body: string } | null {
  const words = s.split(/\s+/);
  let hi = 0;
  while (hi < Math.min(words.length - 2, 15)) {
    const wLow = words[hi].toLowerCase();
    if (hi >= 4 && CLAUSE_STARTERS.has(wLow)) break;
    if (/^[A-Z]/.test(words[hi]) || STOP_SET.has(wLow)) hi++;
    else break;
  }
  if (hi < 3) return null;
  const heading = words.slice(0, hi).join(' ');
  if (!isHeadingPhrase(heading)) return null;
  const body = words.slice(hi).join(' ').trim();
  if (!body || body.length < 10) return null;
  return { heading, body };
}

type Seg = { kind: 'heading'; text: string } | { kind: 'paragraph'; text: string } | { kind: 'bullet'; items: string[] };

function parseToSegments(text: string): Seg[] {
  const clean = text
    .replace(ZWJ_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*(?:Download|Get|Access|Discover how)\s+(?:this|the|how)[\s\S]*$/i, '');
  const sents = sentenceSplit(clean);
  const segs: Seg[] = [];
  let i = 0;

  while (i < sents.length) {
    const s = sents[i];

    // Sentence ending with colon -> list intro, collect following items
    if (/::?\s*$/.test(s)) {
      const items: string[] = [];
      let j = i + 1;
      while (j < sents.length) {
        const n = sents[j];
        const td = parseTermDesc(n);
        if (td) { items.push(`**${td.term}**: ${td.desc}`); j++; continue; }
        if (!/[.!?]$/.test(n.trim()) || (n.split(/\s+/).length <= 20 && items.length > 0)) {
          items.push(n.replace(/[.]\s*$/, '')); j++;
        } else break;
      }
      segs.push({ kind: 'paragraph', text: s.replace(/::+$/, ':') });
      if (items.length) segs.push({ kind: 'bullet', items });
      i = j;
      continue;
    }

    // Term : description -> standalone bullet or append to previous bullet block
    const td = parseTermDesc(s);
    if (td) {
      const last = segs[segs.length - 1];
      if (last?.kind === 'bullet') last.items.push(`**${td.term}**: ${td.desc}`);
      else segs.push({ kind: 'bullet', items: [`**${td.term}**: ${td.desc}`] });
      i++;
      continue;
    }

    // Heading merged with body text -> split and recurse
    const hb = splitHeadingBody(s);
    if (hb) {
      segs.push({ kind: 'heading', text: hb.heading });
      parseToSegments(hb.body).forEach(seg => segs.push(seg));
      i++;
      continue;
    }

    // Standalone title-case heading
    if (isHeadingPhrase(s)) {
      segs.push({ kind: 'heading', text: s });
      i++;
      continue;
    }

    segs.push({ kind: 'paragraph', text: s });
    i++;
  }

  // Merge consecutive paragraph segments so related sentences stay in one <p>
  const merged: Seg[] = [];
  for (const seg of segs) {
    const last = merged[merged.length - 1];
    if (seg.kind === 'paragraph' && last?.kind === 'paragraph') {
      last.text += ' ' + seg.text;
    } else {
      merged.push({ ...seg } as Seg);
    }
  }
  return merged;
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--seasons-space-2)' }}>
      {items.map((item, idx) => (
        <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--seasons-space-3)' }}>
          <svg width="6" height="6" viewBox="0 0 6 6" fill="var(--seasons-color-cyber-blue-300)" aria-hidden="true" style={{ flexShrink: 0, marginTop: '9px' }}>
            <circle cx="3" cy="3" r="3" />
          </svg>
          <span className="type-body-lg" style={{ color: 'var(--seasons-color-on-dark-body)' }} dangerouslySetInnerHTML={{ __html: item.replace(/[.]\s*$/, '') }} />
        </li>
      ))}
    </ul>
  );
}

function FormattedResourceText({ text }: { text: string }) {
  const segs = parseToSegments(text);
  return (
    <>
      {segs.map((seg, idx) => {
        const mt = idx === 0 ? undefined : seg.kind === 'heading' ? 'var(--seasons-space-10)' : undefined;
        if (seg.kind === 'heading') {
          return (
            <p key={idx} className="type-subhead-md" style={{ color: 'var(--seasons-color-on-dark-heading)', marginTop: mt, marginBottom: 'var(--seasons-space-4)' }}>
              {seg.text}
            </p>
          );
        }
        if (seg.kind === 'bullet') {
          return <BulletList key={idx} items={seg.items} />;
        }
        return (
          <p key={idx} className="type-body-lg" style={{ color: 'var(--seasons-color-on-dark-body)', marginTop: mt }} dangerouslySetInnerHTML={{ __html: seg.text }} />
        );
      })}
    </>
  );
}

const TOC_HEADING_RE = /^\s*(quick\s+navigation|table\s+of\s+contents|in\s+this\s+article|topics\s+covered)\s*:?\s*$/i;

/** The dek as a standalone lead paragraph — unless the body's opening text
 * already starts with the same words (Webflow imports often embed the dek
 * as the body's opening sentence, so showing it twice would be redundant).
 * Mirrors the same dedup check the editor's own preview mode already does
 * (components/blog-editor/ResourceEditor.tsx) so what an editor sees while
 * writing matches what actually publishes. */
function ResourceDek({ dek, blocks, hasBody }: { dek?: string; blocks: Block[]; hasBody: boolean }) {
  if (!dek) return null;
  const dekPlain = stripHtml(dek);
  const dekNorm = dekPlain.trim().replace(/\s+/g, ' ').replace(/[…\.]{1,3}$/, '');
  const firstPara = blocks.find((b: any) => ['paragraph', 'lede'].includes(b.type));
  const firstText = ((firstPara as any)?.text || '').replace(/\s+/g, ' ').trim();
  if (dekNorm.length > 20 && firstText.startsWith(dekNorm)) return null;
  const dekFull = dekPlain.trim().replace(/\s+/g, ' ');
  if (dekFull.length > 20 && firstText.startsWith(dekFull)) return null;
  return (
    <p
      // wp2-body-prose carries no layout/color of its own here (type-body-lg
      // + the inline color below already set those) — added purely so a
      // link typed into the dek picks up .wp2-body-prose a's styling, the
      // same as a link in the body blocks just below it.
      className="type-body-lg wp2-body-prose"
      style={{ color: 'var(--seasons-color-on-dark-body)', marginBottom: hasBody ? 'var(--seasons-space-6)' : 0 }}
      dangerouslySetInnerHTML={{ __html: dek }}
    />
  );
}

function ResourceBodyRenderer({ blocks, dek }: { blocks: Block[]; dek?: string }) {
  // Structured content: multiple blocks or has heading/list blocks → full renderer
  const hasStructure = blocks.length > 2 ||
    blocks.some(b => ['h2', 'h3', 'h4', 'ul', 'ol', 'blockquote', 'image'].includes(b.type));

  if (hasStructure) {
    const filtered = blocks.filter((b, i, arr) => {
      if (b.type === 'h2' && TOC_HEADING_RE.test((b as any).text || '')) return false;
      const prev = arr[i - 1];
      if ((b.type === 'ul' || b.type === 'ol') && prev?.type === 'h2' && TOC_HEADING_RE.test((prev as any).text || '')) return false;
      return true;
    });
    return (
      <>
        <ResourceDek dek={dek} blocks={blocks} hasBody={filtered.length > 0} />
        <div className="wp2-body-prose">
          <ReadOnlyEditorProvider blocks={filtered}>
            {filtered.map(block => <BlockRenderer key={block.id} block={block} />)}
          </ReadOnlyEditorProvider>
        </div>
      </>
    );
  }

  // Single-blob content: extract and format
  let raw = blocks
    .filter((b: any) => ['paragraph', 'lede'].includes(b.type))
    .map((b: any) => (b.text || '') as string)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Webflow imports often embed the dek as the opening sentence of the body blob.
  // Strip it so the body doesn't repeat what "About this X" already implies.
  if (dek) {
    // Strip trailing ellipsis (truncated Webflow dek) before prefix-matching
    const dekNorm = stripHtml(dek).trim().replace(/\s+/g, ' ').replace(/[…\.]{1,3}$/, '');
    if (dekNorm.length > 20 && raw.startsWith(dekNorm)) {
      // Advance past any truncated word fragment then through the sentence end
      const after = raw.slice(dekNorm.length);
      const sentenceEnd = after.match(/^[^.!?]*[.!?]\s*/);
      raw = sentenceEnd ? after.slice(sentenceEnd[0].length).trim() : after.replace(/^\W*/, '').trim();
    }
  }

  return (
    <>
      <ResourceDek dek={dek} blocks={blocks} hasBody={!!raw.trim()} />
      {raw.trim() ? (
        // Same wrapper the structured (hasStructure) branch above uses —
        // without it, this short-content fallback never picked up
        // .wp2-body-prose's link styling (or anything else scoped to it),
        // even though a link typed into the body survives into `raw` fine.
        <div className="wp2-body-prose">
          <FormattedResourceText text={raw} />
        </div>
      ) : null}
    </>
  );
}

// ─── Download form ────────────────────────────────────────────────────────────

const HS_PORTAL_ID = '14536254';
const HS_RESOURCE_FORM_GUID = '76fbbd3a-1560-44a0-8c8c-f4525d47e560';

async function submitToHubspot(fields: Record<string, string>, ip: string): Promise<boolean> {
  try {
    const res = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_RESOURCE_FORM_GUID}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: [
            { name: 'firstname',        value: fields.firstName     || '' },
            { name: 'lastname',         value: fields.lastName      || '' },
            { name: 'email',            value: fields.email         || '' },
            { name: 'company',          value: fields.company       || '' },
            { name: 'collateral_title', value: fields.pageName      || '' },
            { name: 'collateral_type',  value: fields.category      || '' },
            { name: 'collateral',       value: fields.pdfUrl        || '' },
            { name: 'ipaddress',        value: ip },
          ],
          context: {
            pageUri:   fields.pageUri  || '',
            pageName:  fields.pageName || '',
            // HubSpot rejects the whole submission with INVALID_IP_ADDRESS if
            // this is present but empty (e.g. /api/get-ip's fetch failed) —
            // omit the key entirely rather than sending ''.
            ...(ip ? { ipAddress: ip } : {}),
          },
        }),
      }
    );
    return res.ok;
  } catch {
    return false;
  }
}

function DownloadForm({ pdfUrl, category, title, slug, visitorIp, onSubmitted }: {
  pdfUrl?: string;
  category?: string;
  title?: string;
  slug?: string;
  visitorIp?: string;
  onSubmitted?: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const firstName = (form.querySelector('#res-first') as HTMLInputElement)?.value || '';
    const lastName  = (form.querySelector('#res-last')  as HTMLInputElement)?.value || '';
    const email     = (form.querySelector('#res-email') as HTMLInputElement)?.value || '';
    const company   = (form.querySelector('#res-company') as HTMLInputElement)?.value || '';

    setSubmitting(true);
    setError(null);

    // Gated — the PDF only opens once the lead is actually captured in
    // HubSpot. Previously this opened immediately and fired the HubSpot
    // call in the background, so a rejected submission (e.g. the
    // INVALID_IP_ADDRESS bug) silently lost the lead while the visitor saw
    // a normal download with no indication anything had failed.
    const ok = await submitToHubspot({
      firstName,
      lastName,
      email,
      company,
      pageName: title    || document.title,
      category: category || '',
      pdfUrl:   pdfUrl   || '',
    }, visitorIp || '');

    setSubmitting(false);

    if (!ok) {
      setError('Something went wrong submitting the form. Please try again.');
      return;
    }

    if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    setSubmitted(true);
    onSubmitted?.();
  }

  if (submitted) {
    return (
      <div className="wp2-form-body" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'var(--seasons-space-5)',
        padding: 'var(--seasons-space-8) var(--seasons-space-8)',
      }}>
        {/* S2 draw-check */}
        <motion.svg width="40" height="40" viewBox="0 0 56 56" fill="none">
          <circle cx="28" cy="28" r={S2_R} stroke="var(--seasons-color-border-default)" strokeWidth="2.5" />
          <motion.circle
            cx="28" cy="28" r={S2_R}
            stroke="var(--seasons-color-success-600)" strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={S2_CIRC}
            initial={{ strokeDashoffset: S2_CIRC, rotate: -90 }}
            animate={{ strokeDashoffset: 0 }}
            style={{ transformOrigin: '28px 28px' }}
            transition={{ duration: 0.7, ease: EASE_IN_OUT }}
          />
          <motion.polyline
            points="18,28 25,35 38,21"
            stroke="var(--seasons-color-success-600)" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" fill="none"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 0.35, ease: EASE_OUT, delay: 0.7 }}
          />
        </motion.svg>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.85 }}
        >
          <h3 className="type-heading-lg" style={{ color: 'var(--seasons-color-text-default)', marginBottom: 'var(--seasons-space-2)' }}>
            Thank you for downloading!
          </h3>
          <p className="type-body-sm" style={{ color: 'var(--seasons-color-text-secondary)' }}>
            The {categoryLabel(category)} opened in a new tab, and we sent a copy of it to your email.
          </p>
        </motion.div>
        <motion.a
          href="/resources"
          className="block-button-link"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.3 }}
        >
          <div className="block-button block-button--md block-button--outline">
            <div className="block-button__text">
              <span className="btn-primary-text">View all resources</span>
              <span className="btn-hover-text" aria-hidden="true">View all resources</span>
            </div>
          </div>
        </motion.a>
      </div>
    );
  }

  return (
    <div className="wp2-form-body" style={{ padding: 'var(--seasons-space-8) var(--seasons-space-8)' }}>
      <form
        aria-label="Download resource"
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--seasons-space-4)',
          // Keep input fields at their original width even though the rail
          // is now wider; the extra space appears as left padding inside
          // the form-body (right-aligned within the wider rail).
          maxWidth: '480px',
          marginLeft: 'auto',
          marginRight: 'auto',
          width: '100%',
        }}
      >
        <div className="wp2-name-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--seasons-space-4)' }}>
          <Input id="res-first" label="First name" required />
          <Input id="res-last"  label="Last name"  required />
        </div>
        <Input id="res-email"   label="Work email"   type="email" required />
        <Input id="res-company" label="Organization" required />
        <button
          type="submit"
          disabled={submitting}
          style={{
            all: 'unset',
            display: 'inline-block',
            cursor: submitting ? 'default' : 'pointer',
            marginTop: 'var(--seasons-space-2)',
            outline: 'revert',
            opacity: submitting ? 0.6 : 1,
          }}
        >
          <div className="block-button block-button--md block-button--midnight">
            <div className="block-button__text">
              <span className="btn-primary-text">{submitting ? 'Submitting…' : 'Download now'}</span>
              <span className="btn-hover-text" aria-hidden="true">{submitting ? 'Submitting…' : 'Download now'}</span>
            </div>
          </div>
        </button>
        {error && (
          <p className="type-caption" style={{ color: 'var(--seasons-color-danger-600, #dc2626)' }}>
            {error}
          </p>
        )}
        <p className="type-caption" style={{ color: 'var(--seasons-color-text-tertiary)' }}>
          By submitting, you confirm that you agree to the processing of your personal data as described in the Privacy Statement.
        </p>
      </form>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const CATEGORY_TO_TAB: Record<string, string> = {
  'White Paper':     'White Papers',
  'Case Study':      'Case Studies',
  'Industry Report': 'Industry Reports',
  'Brochure':        'Brochures',
};

const CATEGORY_TO_URL_SEGMENT: Record<string, string> = {
  'White Paper':     'white-papers',
  'Case Study':      'case-studies',
  'Industry Report': 'industry-reports',
  'Brochure':        'brochures',
};

// "White Paper" → "Whitepaper" (one word) everywhere except the top chip
// which uses the plural tab label e.g. "White Papers"
const CATEGORY_ONE_WORD: Record<string, string> = {
  'White Paper': 'Whitepaper',
};
function chipLabel(category: string | undefined): string {
  return (category && CATEGORY_TO_TAB[category]) || category || 'Resources';
}
function inlineLabel(category: string | undefined): string {
  return (category && CATEGORY_ONE_WORD[category]) || category || 'resource';
}
function navLabel(category: string | undefined): string {
  return (category && CATEGORY_ONE_WORD[category]) || category || 'Resources';
}

export default function ResourceDetailClient({ slug, meta, blocks, related, urlBrand, visitorIp }: Props) {
  const router = useRouter();
  const effectiveBrand = (urlBrand ?? '') || ((meta as { brand?: string }).brand ?? '');
  const isFlow = effectiveBrand === 'flow';
  const isComet = effectiveBrand === 'comet';
  const isGalaxy = effectiveBrand === 'galaxy';
  const isGravity = effectiveBrand === 'gravity';
  const isStoryhealth = effectiveBrand === 'storyhealth';
  const [downloaded, setDownloaded] = useState(false);

  const INNOVACCER_TYPO_RE = /^(Team\s+)?[Ii]?n{1,3}ovac{1,2}e?r{1,2}$/i;
  const BRAND_TEAM_RE      = /^Team\s+(gravity|flow|comet|galaxy|atlas|storyhealth)$/i;
  const EXTRA_GENERIC_SET  = new Set(['team innvoaccer', 'team innovacecr']);
  const isGeneric = (name: string) => { const n = name.trim(); return INNOVACCER_TYPO_RE.test(n) || BRAND_TEAM_RE.test(n) || EXTRA_GENERIC_SET.has(n.toLowerCase()); };
  const authors = Array.isArray(meta.authors)
    ? (meta.authors as { name?: string }[]).filter(a => !isGeneric(a?.name ?? ''))
    : [];
  const pdfUrl = meta.pdfUrl || undefined;
  const heroImg = meta.heroImg as string | undefined;
  const category = meta.category as string | undefined;
  const title = meta.title as string;
  const dek = meta.dek as string | undefined;

  // PDF page sources — load directly from GCS (pre-rendered), no API call needed
  function gcsPageUrl(pdf: string, page: number): string {
    let hash = 0;
    for (let i = 0; i < pdf.length; i++) {
      hash = ((hash << 5) - hash + pdf.charCodeAt(i)) | 0;
    }
    // Use the same md5 hash the API uses — but since we can't run crypto in client,
    // fall back to API URL which redirects to GCS (302) after cache check
    return `/api/pdf-pages?url=${encodeURIComponent(pdf)}&page=${page}`;
  }

  const pageSrcs = pdfUrl
    ? [gcsPageUrl(pdfUrl, 3), gcsPageUrl(pdfUrl, 2), gcsPageUrl(pdfUrl, 1)]
    : [
        'https://storage.googleapis.com/cms_rewire/White-page-Letter%20-%203.png',
        'https://storage.googleapis.com/cms_rewire/White-page-Letter%20-%202.png',
        'https://storage.googleapis.com/cms_rewire/White-page-Letter%20-%201.png',
      ];

  const pdfPages = PAGE_POSES.map((pose, i) => ({ src: pageSrcs[i], ...pose }));

  // Map related posts → ResourceCard[]
  const suggestedCards: ResourceCard[] = related.map(r => ({
    category: r.category || '',
    title: r.title,
    dek: r.dek,
    href: r.category && CATEGORY_TO_URL_SEGMENT[r.category]
      ? `/resources/${CATEGORY_TO_URL_SEGMENT[r.category]}/${r.slug}`
      : `/resources/${r.slug}`,
    imageSrc: r.heroImg || '',
    authorName: r.authorName,
    date: r.date ? formatDate(r.date) : undefined,
    pdfUrl: r.pdfUrl,
    pdfPageCount: r.pdfPageCount,
  }));

  return (
    <div className="wp2-page">
      {isFlow
        ? <FlowNavBar />
        : isComet
        ? <CometNavBar />
        : isGalaxy
        ? <GalaxyNavBar />
        : isGravity
        ? <GravityNavBar />
        : isStoryhealth
        ? <StoryhealthNavBar />
        : <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/request-a-demo" storiesHref="/blogs" />}

      {/* ── Sticky sidebar layout: scrolling left, fixed form right ──
           IMPORTANT: section must NOT have overflow:hidden — that would
           trap sticky positioning and make it have zero effect. ── */}
      <section
        className="wp2-main"
        style={{
          background: 'var(--seasons-color-midnight)',
          position: 'relative',
        }}
      >
        {/* Subtle radial glow */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at top right, var(--seasons-color-cyber-blue) 0%, transparent 60%)',
            opacity: 0.06,
            pointerEvents: 'none',
          }}
        />

        <div className="wp2-grid">

          {/* ── Left hero: category chip · h1 · byline ── */}
          <div className="wp2-left-hero">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: EASE_OUT }}
            >
              {/* Breadcrumb — same muted style as webinar breadcrumb */}
              <div className="wbn-od-breadcrumb">
                <Link href="/resources" className="wbn-od-breadcrumb__link">
                  Resources
                </Link>
                <CaretRight size={12} weight="bold" aria-hidden="true" style={{ color: 'var(--seasons-color-text-tertiary)' }} />
                <Link
                  href={category && CATEGORY_TO_URL_SEGMENT[category] ? `/resources/${CATEGORY_TO_URL_SEGMENT[category]}` : '/resources'}
                  className="wbn-od-breadcrumb__link"
                >
                  {chipLabel(category)}
                </Link>
              </div>

              <h1
                className="type-display-xl wp2-h1"
                style={{
                  color: 'var(--seasons-color-on-dark-heading)',
                  marginBottom: 'var(--seasons-space-4)',
                }}
                dangerouslySetInnerHTML={{ __html: title || '' }}
              />

              {/* Byline */}
              {authors.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--seasons-space-1)',
                  flexWrap: 'wrap',
                }}>
                  <span className="type-body-sm" style={{ color: 'var(--seasons-color-on-dark-body)' }}>By&nbsp;</span>
                  {authors.map((a, i) => (
                    <span key={i} className="type-body-sm">
                      <span style={{ color: 'var(--seasons-color-on-dark-heading)', fontWeight: 500 }}>{a.name || ''}</span>
                      {i < authors.length - 2 && <span style={{ color: 'var(--seasons-color-on-dark-body)' }}>,&nbsp;</span>}
                      {i === authors.length - 2 && <span style={{ color: 'var(--seasons-color-on-dark-body)' }}>&nbsp;and&nbsp;</span>}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* ── Right: sticky form card — spans both left rows on desktop ── */}
          <div className="wp2-right">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.45, ease: EASE_OUT, delay: 0.1 }}
              style={{
                background: 'var(--seasons-color-surface-default)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                // `.wp2-thumb` below enforces a 320px floor and doesn't shrink
                // past it. On short viewports (common on Windows laptops —
                // 1366×768 and similar leave well under 500px of usable
                // height here) the thumb alone can exceed this box's height,
                // which used to hard-clip the download form beneath it —
                // only the first field or two were reachable, with no way to
                // scroll to the rest. Scroll instead of clip so every field
                // stays reachable regardless of viewport height.
                overflowY: 'auto',
              }}
            >
              {/* Thumbnail — hover to fan; "downloaded" elevates sheets to centre */}
              <motion.div
                className={`wp2-thumb${downloaded ? ' wp2-thumb--downloaded' : ''}`}
                initial="rest"
                animate={downloaded ? 'downloaded' : 'rest'}
                whileHover={downloaded ? undefined : 'spread'}
                variants={wpThumb}
                style={{
                  width: '100%',
                  flex: 1,
                  minHeight: '320px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute', inset: 0,
                    background: whitepaperGradient(pdfUrl || slug || heroImg),
                  }}
                />
                {pdfPages.map(({ src, rest, spread, downloaded: dl, z }) => (
                  <motion.img
                    key={`${src}-${z}`}
                    src={src}
                    alt=""
                    aria-hidden="true"
                    fetchPriority={z === 3 ? 'high' : 'low'}
                    loading="eager"
                    className="wp2-page-img"
                    variants={{
                      rest:       { ...rest,   transition: { duration: 0.45, ease: T_PAGE } },
                      spread:     { ...spread, transition: { duration: 0.45, ease: T_PAGE } },
                      downloaded: { ...dl,     transition: { duration: 0.65, ease: EASE_OUT } },
                    }}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      marginLeft: '-22%',
                      width: '44%',
                      height: 'auto',
                      zIndex: z,
                      transformOrigin: 'center bottom',
                      boxShadow: 'var(--seasons-shadow-card)',
                      borderRadius: 'var(--seasons-radius-sm)',
                      display: 'block',
                    }}
                  />
                ))}

                {/* Shimmer sweep — single pass after form submitted */}
                {downloaded && (
                  <motion.div
                    aria-hidden="true"
                    initial={{ x: '-110%', skewX: -18 }}
                    animate={{ x: '320%', skewX: -18 }}
                    transition={{ duration: 0.75, ease: EASE_OUT, delay: 0.5 }}
                    style={{
                      position: 'absolute', top: 0, left: 0, width: '38%', height: '100%',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)',
                      pointerEvents: 'none', zIndex: 20,
                    }}
                  />
                )}
              </motion.div>

              {/* Form — natural height; thumbnail above absorbs leftover space */}
              <div style={{ flexShrink: 0 }}>
                <DownloadForm pdfUrl={pdfUrl} category={category} title={title} slug={slug} visitorIp={visitorIp} onSubmitted={() => setDownloaded(true)} />
              </div>
            </motion.div>
          </div>

          {/* ── Left body: About · content blocks · share ──
               Separate grid child so CSS order can slot it after the form on mobile. */}
          <div className="wp2-left-body">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: EASE_OUT, delay: 0.15 }}
            >
              {(blocks.length > 0 || dek) && (
                <>
                  <h2
                    className="type-heading-xl"
                    style={{ color: 'var(--seasons-color-on-dark-heading)', marginBottom: 'var(--seasons-space-5)' }}
                  >
                    {(meta.bodyHeading as string | undefined) || `About this ${inlineLabel(category)}`}
                  </h2>
                  <ResourceBodyRenderer blocks={blocks} dek={dek} />
                </>
              )}

              {/* Share row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--seasons-space-4)',
                marginTop: 'var(--seasons-space-10)',
              }}>
                <span className="wbn-share-label">Share</span>
                <BlogShareInline title={title} />
              </div>

              {/* Topic tags — below share, dark surface variant */}
              {Array.isArray(meta.tags) && meta.tags.length > 0 && (
                <div className="wp2-topic-tags">
                  <span className="wbn-share-label">Topics</span>
                  {(meta.tags as { tag?: string }[]).map((t) => t.tag && (
                    <a
                      key={t.tag}
                      href={`/resources?q=${encodeURIComponent(t.tag)}`}
                      className="blog-tag blog-tag--muted wp2-tag"
                    >
                      {t.tag}
                    </a>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

        </div>
      </section>

      {/* ── Other resources — full-width horizontal, same category ── */}
      {suggestedCards.length > 0 && (
        <SuggestedResources
          heading="More resources"
          cards={suggestedCards}
          viewAllHref="/resources"
          viewAllLabel="View all resources"
        />
      )}

      {isFlow ? <FlowFooter /> : isComet ? <CometFooter /> : isGalaxy ? <GalaxyFooter /> : isGravity ? <GravityFooter /> : isStoryhealth ? <StoryhealthFooter /> : <Footer theme="light" />}
    </div>
  );
}
